/*
 * Purpose: Encrypted secrets vault — AES-GCM + PBKDF2, tenant-scoped at rest
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import type { Secret } from '@/stores/secretStore';
import { migrateLegacyTenantStorageIfNeeded, vaultStorageKey } from '@/lib/tenantStorage';

/** @deprecated Global key — migrate via tenantStorage only */
export const VAULT_STORAGE_KEY = 'autoblocks.vault.v1';
export const LEGACY_SECRETS_KEY = 'secrets';
export const VAULT_EXPORT_VERSION = 1;

export interface VaultPayload {
  secrets: Array<{
    id: string;
    name: string;
    value: string;
    createdAt: string;
    updatedAt?: string;
    scope?: 'global';
  }>;
}

export interface EncryptedVaultBlob {
  version: number;
  algorithm: 'AES-GCM';
  kdf: 'PBKDF2';
  iterations: number;
  salt: string;
  iv: string;
  ciphertext: string;
}

function getSubtle(): SubtleCrypto {
  if (typeof globalThis.crypto !== 'undefined' && globalThis.crypto.subtle) {
    return globalThis.crypto.subtle;
  }
  throw new Error('Web Crypto API is not available');
}

function randomBytes(length: number): Uint8Array {
  const buf = new Uint8Array(length);
  crypto.getRandomValues(buf);
  return buf;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function deriveKey(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const subtle = getSubtle();
  const enc = new TextEncoder();
  const keyMaterial = await subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, [
    'deriveKey',
  ]);
  return subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptVault(
  payload: VaultPayload,
  passphrase: string,
  iterations = 210_000
): Promise<EncryptedVaultBlob> {
  const salt = randomBytes(16);
  const iv = randomBytes(12);
  const key = await deriveKey(passphrase, salt, iterations);
  const subtle = getSubtle();
  const enc = new TextEncoder();
  const ciphertext = await subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(payload)));

  return {
    version: VAULT_EXPORT_VERSION,
    algorithm: 'AES-GCM',
    kdf: 'PBKDF2',
    iterations,
    salt: toBase64(salt),
    iv: toBase64(iv),
    ciphertext: toBase64(new Uint8Array(ciphertext)),
  };
}

export async function decryptVault(
  blob: EncryptedVaultBlob,
  passphrase: string
): Promise<VaultPayload> {
  const subtle = getSubtle();
  const key = await deriveKey(passphrase, fromBase64(blob.salt), blob.iterations);
  const decrypted = await subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64(blob.iv) },
    key,
    fromBase64(blob.ciphertext)
  );
  const text = new TextDecoder().decode(decrypted);
  return JSON.parse(text) as VaultPayload;
}

export function readVaultBlob(): EncryptedVaultBlob | null {
  if (typeof localStorage === 'undefined') return null;
  migrateLegacyTenantStorageIfNeeded();
  const raw = localStorage.getItem(vaultStorageKey());
  if (!raw) return null;
  try {
    return JSON.parse(raw) as EncryptedVaultBlob;
  } catch {
    return null;
  }
}

export function writeVaultBlob(blob: EncryptedVaultBlob): void {
  migrateLegacyTenantStorageIfNeeded();
  localStorage.setItem(vaultStorageKey(), JSON.stringify(blob));
}

export function hasLegacySecrets(): boolean {
  return Boolean(typeof localStorage !== 'undefined' && localStorage.getItem(LEGACY_SECRETS_KEY));
}

export function migrateLegacySecrets(): Secret[] {
  const raw = localStorage.getItem(LEGACY_SECRETS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(atob(raw)) as Secret[];
    localStorage.removeItem(LEGACY_SECRETS_KEY);
    return parsed.map((s) => ({
      ...s,
      createdAt: s.createdAt ? new Date(s.createdAt) : new Date(),
      updatedAt: new Date(),
      scope: 'global' as const,
    }));
  } catch {
    localStorage.removeItem(LEGACY_SECRETS_KEY);
    return [];
  }
}

export function payloadFromSecrets(secrets: Secret[]): VaultPayload {
  return {
    secrets: secrets.map((s) => ({
      id: s.id,
      name: s.name,
      value: s.value,
      createdAt: s.createdAt instanceof Date ? s.createdAt.toISOString() : String(s.createdAt),
      updatedAt: s.updatedAt?.toISOString(),
      scope: 'global',
    })),
  };
}

export function secretsFromPayload(payload: VaultPayload): Secret[] {
  return payload.secrets.map((s) => ({
    id: s.id,
    name: s.name,
    value: s.value,
    createdAt: new Date(s.createdAt),
    updatedAt: s.updatedAt ? new Date(s.updatedAt) : new Date(),
    scope: 'global' as const,
  }));
}

export async function exportVaultFile(secrets: Secret[], passphrase: string): Promise<string> {
  const blob = await encryptVault(payloadFromSecrets(secrets), passphrase);
  return JSON.stringify(
    {
      ...blob,
      exportedAt: new Date().toISOString(),
      app: 'autoblocks',
      note: 'Encrypted vault — reuse across AutoBlocks projects. Do not commit to git.',
    },
    null,
    2
  );
}

export async function importVaultFile(
  json: string,
  passphrase: string
): Promise<Secret[]> {
  const parsed = JSON.parse(json) as EncryptedVaultBlob;
  const payload = await decryptVault(parsed, passphrase);
  return secretsFromPayload(payload);
}
